import { Product, StockStatus } from '../types';
import { bundleContentsText, bundleItemsFor, isBundle, resolveBundle } from './bundles';
import { ProductCategory } from './vat';

/**
 * Butikens struktur: sektioner per kategori, lagerstatus, nedräkning och
 * raden under produktnamnet. Samma regler på shopsidan och produktsidan.
 */

export const STOCK_LABELS: Record<StockStatus, string> = {
    in_stock: 'I lager',
    low: 'Få kvar',
    very_low: 'Nästan slut',
    out: 'Slut i lager',
};

/** Grön, gul, röd och grå, som i Jonathans lista. */
export const STOCK_TONE: Record<StockStatus, 'green' | 'yellow' | 'red' | 'gray'> = {
    in_stock: 'green',
    low: 'yellow',
    very_low: 'red',
    out: 'gray',
};

export const stockOf = (p?: Pick<Product, 'stockStatus'> | null): StockStatus => p?.stockStatus || 'in_stock';

/**
 * Kan produkten köpas? Slut i lager går inte, och inte heller ett paket där
 * någon del är slut. Databasen kontrollerar samma sak när ordern läggs.
 */
export const isPurchasable = (p: Product, catalog: Product[] = []): boolean => {
    if (stockOf(p) === 'out') return false;
    if (!isBundle(p)) return true;
    const byId = new Map(catalog.map(x => [x.id, x]));
    const items = [...(p.bundleItems || []), ...(p.variants || []).flatMap(v => v.bundleItems || [])];
    return items.every(i => stockOf(byId.get(i.productId)) !== 'out');
};

/** Lagerstatus att visa: paket ärver sämsta status bland delarna. */
export const effectiveStock = (p: Product, catalog: Product[] = []): StockStatus => {
    const order: StockStatus[] = ['in_stock', 'low', 'very_low', 'out'];
    let worst = stockOf(p);
    if (isBundle(p)) {
        const byId = new Map(catalog.map(x => [x.id, x]));
        const items = [...(p.bundleItems || []), ...(p.variants || []).flatMap(v => v.bundleItems || [])];
        for (const i of items) {
            const s = stockOf(byId.get(i.productId));
            if (order.indexOf(s) > order.indexOf(worst)) worst = s;
        }
    }
    return worst;
};

/** Raden under namnet: admins text, annars paketets innehåll. */
export const cardSubtitle = (p: Product, catalog: Product[] = []): string | undefined => {
    if (p.subtitle?.trim()) return p.subtitle.trim();
    if (!isBundle(p)) return undefined;
    const variant = p.variants?.find(v => (v.bundleItems?.length ?? 0) > 0);
    const parts = resolveBundle(bundleItemsFor(p, variant), catalog, []);
    return parts.length ? bundleContentsText(parts.map(x => ({ name: x.name, quantity: x.quantity }))) : undefined;
};

export interface ShopSection {
    /** Ankare för kategoriraden, t.ex. "sektion-kaffe". */
    id: string;
    title: string;
    products: Product[];
}

const slug = (s: string) =>
    s.toLocaleLowerCase('sv-SE').replace(/[åä]/g, 'a').replace(/ö/g, 'o').replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '') || 'sektion';

/**
 * En sektion per kategori i kategoriernas ordning (sort_order, sedan namn).
 * Paket (utan egen kategori) kommer efter den första kategorin. Tomma
 * sektioner visas inte.
 */
export const buildShopSections = (products: Product[], categories: ProductCategory[]): ShopSection[] => {
    const sorted = [...categories].sort((a, b) => (a.sortOrder ?? 0) - (b.sortOrder ?? 0) || a.name.localeCompare(b.name, 'sv'));
    const byCategory = sorted
        .map(c => ({ id: `sektion-${slug(c.name)}`, title: c.name, products: products.filter(p => !isBundle(p) && p.categoryId === c.id) }))
        .filter(s => s.products.length > 0);
    const bundles = products.filter(isBundle);
    if (bundles.length === 0) return byCategory;
    const paket: ShopSection = { id: 'sektion-paket', title: 'Paket', products: bundles };
    return byCategory.length === 0 ? [paket] : [byCategory[0], paket, ...byCategory.slice(1)];
};

/**
 * Bästsäljare: produkter som bara finns i den här gruppens butik (t.ex.
 * Bankerydspaketet) läggs först.
 */
export const pinGroupProducts = (shown: Product[], all: Product[], classId?: string | null, limit = shown.length): Product[] => {
    if (!classId) return shown;
    const pinned = all.filter(p => (p.visibleClassIds || []).includes(classId));
    if (pinned.length === 0) return shown;
    const ids = new Set(pinned.map(p => p.id));
    return [...pinned, ...shown.filter(p => !ids.has(p.id))].slice(0, Math.max(limit, pinned.length));
};

/** "8 dagar 14 h", "14 h 20 min", "20 min". null när kampanjen är slut. */
export const countdownText = (end: number, now: number = Date.now()): string | null => {
    const ms = end - now;
    if (ms <= 0) return null;
    const min = Math.floor(ms / 60000);
    const days = Math.floor(min / 1440);
    const hours = Math.floor((min % 1440) / 60);
    const mins = min % 60;
    if (days > 0) return `${days} ${days === 1 ? 'dag' : 'dagar'} ${hours} h`;
    if (hours > 0) return `${hours} h ${mins} min`;
    return `${Math.max(1, mins)} min`;
};

/** Paket i butiken som innehåller produkten ("Spara med ett paket"). Slutsålda paket visas inte. */
export const bundlesContaining = (product: Product, products: Product[]): Product[] =>
    products.filter(p =>
        p.id !== product.id &&
        isBundle(p) &&
        isPurchasable(p, products) &&
        [...(p.bundleItems || []), ...(p.variants || []).flatMap(v => v.bundleItems || [])].some(i => i.productId === product.id));

/**
 * "Du kanske också gillar": produkterna admin valt (som finns i butiken),
 * annars andra produkter ur gruppens butik, samma kategori först. Högst 4.
 */
export const relatedFor = (product: Product, products: Product[], max = 4): Product[] => {
    const others = products.filter(p => p.id !== product.id);
    const chosen = (product.relatedProductIds || []).map(id => others.find(p => p.id === id)).filter((p): p is Product => !!p);
    if (chosen.length > 0) return chosen.slice(0, max);
    const same = others.filter(p => p.categoryId && p.categoryId === product.categoryId);
    const rest = others.filter(p => !same.includes(p));
    return [...same, ...rest].slice(0, max);
};

export interface ReviewLike { rating: number; productId?: string; published: boolean }

/** Publicerade recensioner för produkten, och snittbetyget (en decimal). */
export const reviewsFor = <R extends ReviewLike>(productId: string, reviews: R[]): { list: R[]; average: number | null } => {
    const list = reviews.filter(r => r.published && r.productId === productId);
    const average = list.length ? Math.round((list.reduce((s, r) => s + r.rating, 0) / list.length) * 10) / 10 : null;
    return { list, average };
};
